export type SongSource = 'apple' | 'spotify' | 'tidal' | 'local' | 'manual';

export interface CatalogIds {
  apple?: string;
  spotify?: string;
  tidal?: string;
}

export interface SongIdentity {
  source: SongSource;
  catalogIds: CatalogIds;
  isrc: string | null;
}

export interface SongIdentityInput {
  song: {
    source?: SongSource;
    catalogIds?: CatalogIds | null;
    isrc?: string | null;
    itunesTrackId?: string | number | null;
  };
  platformLink?: string | null;
  platformName?: string | null;
  spotifyUri?: string | null;
  isLocalFile?: boolean;
}

const SPOTIFY_WEB_TRACK = /open\.spotify\.com\/(?:intl-[a-z]{2}(?:-[a-z]{2})?\/)?track\/([A-Za-z0-9]+)/i;
const SPOTIFY_URI_TRACK = /^spotify:track:([A-Za-z0-9]+)$/;
const TIDAL_TRACK = /tidal\.com\/(?:browse\/)?track\/(\d+)/i;
const APPLE_SONG_PARAM = /music\.apple\.com\/.*[?&]i=(\d+)/i;
const APPLE_SONG_PATH = /music\.apple\.com\/(?:[a-z]{2}\/)?song\/(?:[^/?#]+\/)?(\d+)/i;

export function spotifyTrackIdFrom(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  const match = trimmed.match(SPOTIFY_URI_TRACK) ?? trimmed.match(SPOTIFY_WEB_TRACK);
  return match ? match[1] : null;
}

export function tidalTrackIdFrom(value: string | null | undefined): string | null {
  if (!value) return null;
  const match = value.trim().match(TIDAL_TRACK);
  return match ? match[1] : null;
}

export function appleTrackIdFrom(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  const match = trimmed.match(APPLE_SONG_PARAM) ?? trimmed.match(APPLE_SONG_PATH);
  return match ? match[1] : null;
}

function cleanCatalogIds(ids: CatalogIds | null | undefined): CatalogIds {
  const result: CatalogIds = {};
  if (!ids) return result;
  if (typeof ids.apple === 'string' && ids.apple) result.apple = ids.apple;
  if (typeof ids.spotify === 'string' && ids.spotify) result.spotify = ids.spotify;
  if (typeof ids.tidal === 'string' && ids.tidal) result.tidal = ids.tidal;
  return result;
}

export function normalizeSongIdentity(input: SongIdentityInput): SongIdentity {
  const catalogIds = cleanCatalogIds(input.song.catalogIds);

  const itunesId = input.song.itunesTrackId;
  const appleId = itunesId !== null && itunesId !== undefined && String(itunesId) !== ''
    ? String(itunesId)
    : appleTrackIdFrom(input.platformLink);
  if (appleId) catalogIds.apple = appleId;

  const spotifyId = spotifyTrackIdFrom(input.platformLink) ?? spotifyTrackIdFrom(input.spotifyUri);
  if (spotifyId) catalogIds.spotify = spotifyId;

  const tidalId = tidalTrackIdFrom(input.platformLink);
  if (tidalId) catalogIds.tidal = tidalId;

  return {
    source: input.song.source ?? inferSource(input, catalogIds, spotifyId, tidalId),
    catalogIds,
    isrc: input.song.isrc ?? null,
  };
}

function inferSource(
  input: SongIdentityInput,
  catalogIds: CatalogIds,
  spotifyId: string | null,
  tidalId: string | null,
): SongSource {
  if (input.isLocalFile) return 'local';
  if (catalogIds.apple) return 'apple';
  if (spotifyId) return 'spotify';
  if (tidalId) return 'tidal';
  return 'manual';
}

export function isrcForMatchedSong(
  result: { trackId: number; isrc?: string | null } | null,
  existingSong: { itunesTrackId?: string | number | null; isrc?: string | null },
): string | null {
  if (!result || !result.trackId) return null;
  if (result.isrc) return result.isrc;
  if (String(result.trackId) === String(existingSong.itunesTrackId) && existingSong.isrc) return existingSong.isrc;
  return null;
}
